import { DateTime } from "luxon";
import {
  required, between, helpers, requiredIf
} from "vuelidate/lib/validators";
import axios from "axios";
import initialState from "./store/modules/settings";

// force the domain of the email to be lowercase and send it to the email validator
export const emailFormatter = (value) => {
  if (!value) return value;
  const parts = value.split("@");
  parts[1] = parts[1].toLowerCase();
  return parts.join("@");
};

// check address to validate if it contains po box
export const validateAddress = (value) => {
  if (!value) return true;

  const search = value.match(/p[.\s]?o[.\s*]?\s*?box/i);

  return (search === null);
};

// check email to verify valid email with dns
export const emailCheck = async (value) => {
  if (!value) return true;

  const { status } = await axios.post(
    `${initialState.state.apiUrl}api/email-check`,
    { email: value },
    { withCredentials: true }
  );

  return status === 200;
};

const validations = {
  requestamount: {
    required,
    between: between(200, 5000),
  },
  state: { required },
  loan_purpose: { required },
  firstname: { required },
  lastname: { required },
  email: {
    required,
    email: (val) => emailCheck(val),
  },
  homephone: {
    required,
    phone: helpers.regex("phone", /^\(?[2-9][0-9]{2}\)?[-. ]?[2-9][0-9]{2}[-. ]?[0-9]{4}$/),
  },
  address: {
    required,
    address: (val) => validateAddress(val),
  },
  address1: {
    address: (val) => validateAddress(val),
  },
  city: {
    required,
    lettersAndSpaces: helpers.regex("lettersAndSpaces", /^[0-9A-Za-z-'.\s]+$/),
  },
  zip: {
    required,
    zip: helpers.regex("zip", /^((\d{5}-\d{4})|(\d{5})|([A-Z]\d[A-Z]\s\d[A-Z]\d))$/),
  },
  rentorown: { required },
  addressyears: {
    required,
    between: between(0, 99),
  },
  addressmonths: {
    required,
    between: between(0, 12),
  },
  dlstate: { required },
  dlnumber: { required },
  ismilitary: { required },
  hiredate: {
    required,
    // must be hired more than 60 days ago
    minValue: (value) => value < DateTime.local().minus({ days: 60 }).toISODate(),
  },
  empphone: {
    required,
    phone: helpers.regex("phone", /^\(?[2-9][0-9]{2}\)?[-. ]?[2-9][0-9]{2}[-. ]?[0-9]{4}$/),
  },
  empstate: { required },
  empzip: {
    required,
    zip: helpers.regex("zip", /^((\d{5}-\d{4})|(\d{5})|([A-Z]\d[A-Z]\s\d[A-Z]\d))$/),
  },
  incometype: {
    required: (value) => value.toLowerCase() !== "select",
  },
  paytype: { required },
  payfrequency: { required },
  lastpaydate: {
    required,
    // must be a date in the past
    minValue: (value) => value < DateTime.local().toISODate(),
  },
  nextpaydate: {
    required,
    // a date in the past but before our calculated next payment
    validNextPaydate: (value) => value > DateTime.local().toISODate(),
  },
  grossmonthly: {
    required,
    between: between(100, 999999),
  },
  bankname: { required },
  accounttype: { required },
  routingnumber: {
    required,
    min: (value) => value.length === 9,
    max: (value) => value.length === 9,
  },
  accountnumber: { required },
  ssn: {
    required,
    min: (value) => value.length === 9,
  },
  iscitizen: { required },
  dob: {
    required, // must be a date in the past
    minValue: (value) => value <= DateTime.local().plus({ years: -18 }).toISODate(),
    maxValue: (value) => value >= DateTime.local().plus({ years: -140 }).toISODate(),
  },
  allow_credit_check: {
    required,
    checked: value => value === true,
  },
  consent_status: {
    required,
    checked: value => value === true,
  },
  term_condition: {
    required,
    checked: value => value === true,
  },
  how_long_work_here: { required },
  empname: {
    required: requiredIf((value) => {
      const { employmentType } = value;
      return !["employed"].includes(employmentType);
    }),
  },
  employmentType: { required },
  usa_patriot_act: {
    required,
    checked: value => value === true,
  },
};

export function getValidationsFor(formData) {
  return Object.keys(formData).reduce((acc, key) => {
    if (validations[key]) {
      acc[key] = validations[key];
    }
    return acc;
  }, {});
}

export default validations;
